import React, { useCallback, useRef, useState } from 'react'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { LoadingScreen } from './LoadingScreen'

/**
 * Reusable hook to open external URLs inside a controlled iframe dialog with:
 *  - Minimum perceived loading time (to prevent flash / layout jank)
 *  - Single open at a time (subsequent open replaces URL)
 *  - Cleanup of timers on unmount / close
 *  - Optional callbacks for telemetry
 */
export interface UseExternalLinkDialogOptions {
  /** Minimum ms the loading state should remain visible (default 2500) */
  minVisibleMs?: number
  /** Called when a link is opened */
  onOpen?: (url: string) => void
  /** Called after iframe first onLoad finishes and loading overlay is removed */
  onLoaded?: (url: string, loadDurationMs: number, gatedExtraMs: number) => void
  /** Called when dialog closes */
  onClose?: (url: string | null, openDurationMs: number | null) => void
  /** Fullscreen mode hint (small screens) */
  fullScreen?: boolean
  /** Override dialog maxWidth */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /** Optional title element or null to omit */
  title?: React.ReactNode
}

export interface ExternalLinkDialogController {
  open: (url: string, opts?: { label?: string }) => void
  close: () => void
  isOpen: boolean
  isLoading: boolean
  Dialog: React.ReactElement | null
}

export function useExternalLinkDialog(opts: UseExternalLinkDialogOptions = {}): ExternalLinkDialogController {
  const {
    minVisibleMs = 2500,
    onOpen,
    onLoaded,
    onClose,
    fullScreen = false,
    maxWidth = 'xl',
    title = null,
  } = opts

  const [open, setOpen] = useState(false)
  const [src, setSrc] = useState('')
  const [loading, setLoading] = useState(false)
  const [label, setLabel] = useState<string | undefined>(undefined)

  // Refs for timing / lifecycle
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const revealTimerRef = useRef<number | null>(null)
  const openStartRef = useRef<number>(0)
  const loadStartRef = useRef<number>(0)
  const firstLoadHandledRef = useRef<boolean>(false)

  const clearTimers = () => {
    if (revealTimerRef.current) {
      window.clearTimeout(revealTimerRef.current)
      revealTimerRef.current = null
    }
  }

  const close = useCallback(() => {
    clearTimers()
    const openDuration = openStartRef.current ? Date.now() - openStartRef.current : null
    setOpen(false)
    setSrc('')
    setLoading(false)
    firstLoadHandledRef.current = false
    if (onClose) onClose(src || null, openDuration)
  }, [onClose, src])

  const openLink = useCallback((url: string, openOpts?: { label?: string }) => {
    clearTimers()
    openStartRef.current = Date.now()
    loadStartRef.current = Date.now()
    firstLoadHandledRef.current = false
    setSrc(url)
    setLabel(openOpts?.label?.trim() || undefined)
    setLoading(true)
    setOpen(true)
    if (onOpen) onOpen(url)
  }, [onOpen])

  const handleIframeLoad = useCallback(() => {
    if (firstLoadHandledRef.current) return
    firstLoadHandledRef.current = true
    const elapsed = Date.now() - loadStartRef.current
    const extra = Math.max(minVisibleMs - elapsed, 0)
    if (extra === 0) {
      setLoading(false)
      if (onLoaded) onLoaded(src, elapsed, 0)
      return
    }
    revealTimerRef.current = window.setTimeout(() => {
      setLoading(false)
      if (onLoaded) onLoaded(src, elapsed, extra)
      revealTimerRef.current = null
    }, extra)
  }, [minVisibleMs, onLoaded, src])

  const DialogEl = open ? (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={maxWidth}
      sx={{ height: '100%', '& .MuiDialog-paper': { borderRadius: fullScreen ? 0 : '20px', height: '100%' } }}
    >
      {/* Always show a top bar with close button; include title content if provided */}
      <DialogTitle sx={{
        borderBottom: theme => `1px solid ${theme.palette.primary.main}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 64, py: 1,
      }}>
        <Box sx={{ flex: 1, textAlign: 'left', pr: 2, minHeight: 24 }}>
          {title}
        </Box>
        <IconButton
          aria-label="close dialog"
          onClick={close}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          {loading && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, backgroundColor: 'white' }}>
              <LoadingScreen />
            </Box>
          )}
          <iframe
            ref={iframeRef}
            src={src}
            title={label ? `${label}` : 'External content'}
            aria-label={label ? `${label} (embedded content)` : 'External embedded content'}
            style={{ border: 0, width: '100%', height: '100%', display: loading ? 'none' : 'block' }}
            allow="clipboard-read; clipboard-write;"
            onLoad={handleIframeLoad}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ mb: 1, mr: 1 }} />
    </Dialog>
  ) : null

  return {
    open: openLink,
    close,
    isOpen: open,
    isLoading: loading,
    Dialog: DialogEl,
  }
}
